<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("page.event_details.contact_info") }}</template>
        <template v-slot:body>
            <div :class="containerHeightClass">
                <p class="mb-3">{{ translate("page.admin_edit_event.add_contact") }}</p>
                <div :class="getFormGroupClass('email1')">
                    <label class="label">{{ translate("common.email") }}:</label>
                    <div class="control">
                        <input class="input" id="email1" type="text" v-model="email" />
                        <p class="help">
                            {{ getValidationMessage("email1") }}
                        </p>
                    </div>
                </div>
                <div :class="getFormGroupClass('phone1')">
                    <label class="label">{{ translate("form.phone") }}</label>
                    <div class="control">
                        <PhoneNumberInput
                            id="phone1"
                            v-model="phone"
                            @dropdownOpen="phoneDropdownOpen"
                            @dropdownClose="phoneDropdownClose"
                        />
                        <p class="help">
                            {{ getValidationMessage("phone1") }}
                        </p>
                    </div>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <div>
                <button class="button" @click="close">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton :disabled="!formValid" :is-loading="savingInProgress" class="button is-primary" @click="save">
                    {{ translate("common.save") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref, computed, onMounted } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import PhoneNumberInput from "../Common/PhoneNumberInput.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import { useModal } from "../Common/useModal";
    import { useEditEventStore } from "./editEventStore";
    import events from "../Api/events";
    import history from "../Common/history";
    import type { ContactInfo } from "../Common/types";

    const historyInstance = history.getInstance(window);

    const store = useEditEventStore();
    const { open: openModal, close: closeModal, modal } = useModal();

    const email = ref<string>("");
    const phone = ref<string>("");
    const savingInProgress = ref<boolean>(false);
    const containerHeightClass = ref<string>("");
    const validationErrors = ref<Record<string, string[]>>({});

    const formValid = computed(() => {
        return !!email.value || !!phone.value;
    });

    function open() {
        const contactInfo: ContactInfo | null = store.event.contactInfo ?? null;

        email.value = contactInfo?.email1 || "";
        phone.value = contactInfo?.phone1 || "";

        openModal();
    }

    function save() {
        savingInProgress.value = true;
        return events
            .updateContactInfo(store.eventId, {
                email1: email.value,
                phone1: phone.value,
            })
            .then(() => {
                store.setEventProperty({
                    name: "contactInfo",
                    value: {
                        email1: email.value,
                        phone1: phone.value,
                    },
                });
                successToast(translate("page.admin_edit_event.contact_saved"));
                closeModal();
            })
            .catch(err => {
                if (err.validationErrors) {
                    updateValidationErrors(err.validationErrors);
                }

                handleError(err);
            })
            .finally(() => {
                savingInProgress.value = false;
            });
    }

    function close() {
        closeModal();
    }

    function phoneDropdownOpen() {
        containerHeightClass.value = "extended-height-for-dropdown";
    }

    function phoneDropdownClose() {
        containerHeightClass.value = "";
    }

    function updateValidationErrors(errors: Array<{ field: string; message: string[] }>) {
        errors.forEach(valErr => {
            validationErrors.value[valErr.field] = valErr.message;
        });
    }

    function getFormGroupClass(field: string): string {
        if (validationErrors.value[field]) {
            return "field has-error";
        }

        return "field";
    }

    function getValidationMessage(field: string): string {
        return validationErrors.value[field] ? validationErrors.value[field].join(" ") : "";
    }

    onMounted(() => {
        const actionQueryParam = historyInstance.getQueryParam("action");
        if (actionQueryParam === "contactInfo") {
            open();
        }
    });

    defineExpose({
        open,
    });
</script>

<style scoped>
    .extended-height-for-dropdown {
        height: 400px;
    }
</style>
